import React, { useState } from "react";
import { Container } from "@material-ui/core";
import { KeyboardArrowDown } from "@material-ui/icons";
import { NoPaddingBottomGrid } from "../../DataTable/DataTableStyledComponents";
import Heading from "../../../ui/Heading/Heading";
import { variables } from "../../../theme/variables";
import { useTranslation } from "react-i18next";
import DragAndDrop, {
  DndError,
  DndState,
} from "../../DragAndDropV2/DragAndDrop";
import { DelegeeData } from "../../../context/store/reducers/importDelegations.reducer";
import useImportDelegationsContext from "../../../context/hooks/useImportDelegationsContext";
import { isEmpty } from "underscore";
import { ICheckParkingRight } from "../../DelegationForm/ParkingRightsGrid/ParkingRightsGrid";
import useAppContext from "../../../context/hooks/useAppContext";
import { ValidateDelegeesRequestModel } from "../../../models/bulkImport/ValidateDelegeesRequestModel";
import { GeneralValidationError, InsufficientAvailabilityError, ValidateDelegeesResponseModel } from "../../../models/bulkImport/ValidateDelegeesResponseModel";
import { readDelegeesExcel } from "../delegee-import-excel";
import { produce } from "immer";
import { Product } from "../../../api/entryRights.api";
import { AxiosResponse } from "axios";
import { getLocalizedDate } from "../../../utils/date.utils";

const { typography } = variables;

interface DelegeesImportDragAndDropProps {
  show: boolean;
  onFileProcessed: (data: DelegeeData[]) => void;
  onCheckedParkingRights: (pr: ICheckParkingRight[]) => void;
  onProcessing: (processing: boolean) => void;
  validateDelegees: (
    seasonTicketOwnerCrmId: string,
    req: ValidateDelegeesRequestModel,
    onSuccess: (response: ValidateDelegeesResponseModel) => void,
    onError: (errorCode: string) => void) => void;
  getDelegeesValidationResult: (
    seasonTicketOwnerCrmId: string,
    fileReference: string,
    language: string) => Promise<AxiosResponse<Blob>>;
  getDelegeesInvitationResult: (
    seasonTicketOwnerCrmId: string,
    fileReference: string,
    language: string) => Promise<AxiosResponse<Blob>>;
  translationContextName: string;
}

const DelegeesImportDragAndDrop: React.FC<DelegeesImportDragAndDropProps> = ({
  onProcessing,
  onFileProcessed,
  onCheckedParkingRights,
  validateDelegees,
  getDelegeesValidationResult,
  getDelegeesInvitationResult,
  show,
  translationContextName
}) => {
  const [state, setState] = useState<DndState>("default");
  const [error, setError] = useState<DndError | undefined>(undefined);
  const [fileReference, setFileReference] = useState<string>();
  const [generalValidationErrors, setGeneralValidationErrors] = useState<GeneralValidationError[]>();
  const [insufficientAvailabilityErrors, setInsufficientAvailabilityErrors] = useState<InsufficientAvailabilityError[]>();
  const [invalidDelegeesCount, setInvalidDelegeesCount] = useState<number>();
  const [warningDelegeesCount, setWarningDelegeesCount] = useState<number>();
  const [validateDelegeesErrorCode, setValidateDelegeesErrorCode] = useState<string>();
  const { appState } = useAppContext();

  const { importDelegationsState } = useImportDelegationsContext();

  const { t } = useTranslation(["delegeeForm"]);
  const supportedFileTypes = [
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "application/vnd.ms-excel",
  ];

  const handleFileUpload = (file: File) => {
    setState("processing");
    onProcessing(true);
    readDelegeesFromXls(file);
  };

  const readDelegeesFromXls = (file: File) => {
    const reader = new FileReader();
    reader.readAsBinaryString(file);
    reader.onload = handleUploadedData;
  };

  const handleUploadedData = (e: ProgressEvent<FileReader>) => {
    const readResult = readDelegeesExcel(e.target!);
    if (readResult.ok) {
      const delegeesToImport = readResult.data;

      const fileIsEmpty = isEmpty(delegeesToImport);
      if (fileIsEmpty) {
        setState("error");
        setError("empty");
        onProcessing(false);
        return;
      }

      const updatedCheckParkingRights = produce(importDelegationsState.checkParkingRights, draft => {
        draft.forEach(checkParkingRight => {
          checkParkingRight.right!.totalDelegatedParkingRights! += delegeesToImport.length;
        });
      });

      onCheckedParkingRights(updatedCheckParkingRights);

      const req: ValidateDelegeesRequestModel = {
        delegees: delegeesToImport.map(d => ({ name: d.name.value, emailAddress: d.emailAddress.value, lineNumber: d.lineNumber ?? 0 })),
        products: updatedCheckParkingRights.map(pr => ({ 
          pmcId: pr.right?.pmcId!, 
          contractType: pr.right?.contractType!, 
          productType: pr.right?.productType!,
          activationDate: getLocalizedDate(pr.activationDate),
          expirationDate: getLocalizedDate(pr.expirationDate, true)
        })),
        userEmail: appState.user.email
      };

      validateDelegees(
        appState.user.seasonTicketOwnerCrmId as string,
        req,
        (data) => {
          setState("success");
          if (!data.hasErrors) {
            onProcessing(false);
            onFileProcessed(delegeesToImport);

            // Success, but the backend flagged warnings (e.g. Activation Date kept unchanged on a re-upload).
            // Warnings are written to the same validation result file, so reuse it for the download.
            if (data.numberOfWarnings > 0) {
              if (data.fileReference) {
                setFileReference(data.fileReference);
              }
              setWarningDelegeesCount(data.numberOfWarnings);
            }
          }
          else {
            setState("error");
            setError("invalid");

            if (data.fileReference) {
              setFileReference(data.fileReference);
            }
            setGeneralValidationErrors(data.generalErrors);
            setInsufficientAvailabilityErrors((data.insufficientAvailabilityDetails ?? []).map(detail => {
              const parkingRight = updatedCheckParkingRights.find(
                checkParkingRight => checkParkingRight.right?.pmcId === detail.pmcId
              )?.right;

              return {
                ...detail,
                location: parkingRight?.location ?? `${detail.pmcId}`,
                pmcName: parkingRight?.pmcName ?? "",
              };
            }));
            setInvalidDelegeesCount(data.numberOfErrors);
          }
        },
        (errorCode) => {
          setState("error");
          setError("invalid");

          setValidateDelegeesErrorCode(errorCode)
        });

    } else {
      setState("error");
      setError("invalidFile");
    }
  };

  if (!show) {
    return null;
  }

  return (
    <React.Fragment>
      {error === undefined && (<Container>
        <NoPaddingBottomGrid container item direction="row" alignItems="center">
          <NoPaddingBottomGrid container item direction="column" xs={6}>
            <Heading fontSize={typography.fontSizeBase}>
              <strong>{t("uploadFile")}</strong>
            </Heading>
          </NoPaddingBottomGrid>
          <NoPaddingBottomGrid container item direction="column" xs={6}>
            <div className="download-template">
              <div>
                <a
                  href="/files/DelegeeImportTemplate.xlsx"
                  target="_blank"
                  download
                >
                  {t("downloadTemplate")}
                </a>
                <KeyboardArrowDown />
              </div>
            </div>
          </NoPaddingBottomGrid>
        </NoPaddingBottomGrid>
      </Container>)}
      <Container className="h-full">
        <DragAndDrop
          onFileUploaded={handleFileUpload}
          supportedFileTypes={supportedFileTypes}
          state={state}
          error={error}
          seasonTicketOwnerCrmId={appState.user.seasonTicketOwnerCrmId as string}
          fileReference={fileReference}
          invalidDelegeesCount={invalidDelegeesCount}
          warningDelegeesCount={warningDelegeesCount}
          validateDelegeesErrorCode={validateDelegeesErrorCode}
          generalValidationErrors={generalValidationErrors}
          insufficientAvailabilityErrors={insufficientAvailabilityErrors}
          showMaxFileSize
          getDelegeesValidationResult={getDelegeesValidationResult}
          getDelegeesInvitationResult={getDelegeesInvitationResult}
          translationContextName={translationContextName}
        />
      </Container>
    </React.Fragment>
  );
};

export default DelegeesImportDragAndDrop;
